import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChatCircleDots, PaperPlaneRight, MagnifyingGlass, UserPlus, X,
  CircleNotch, Check, ArrowLeft, DotOutline, Users,
} from "@phosphor-icons/react";
import api, { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import UserLink from "../components/UserLink";

/**
 * MessagesPage — two-pane friend chat.
 *
 * Left pane: pending friend requests + threads (friends) with unread badges + "Add Friend".
 * Right pane: active conversation with message history + composer.
 *
 * Polls every 3 seconds while a thread is open — WebSocket upgrade
 * lands with the real-time push in Phase 4.
 */
export default function MessagesPage() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [selected, setSelected] = useState(null); // friend user object
  const [addOpen, setAddOpen] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const wantedUid = searchParams.get("user");

  const loadThreads = async () => {
    try {
      const [tRes, rRes] = await Promise.all([
        api.get("/dm/threads"),
        api.get("/friends/requests"),
      ]);
      setThreads(tRes.data || []);
      setRequests(rRes.data || { incoming: [], outgoing: [] });
    } catch { /* ignore */ }
    setLoadingList(false);
  };

  useEffect(() => {
    loadThreads();
    const t = setInterval(loadThreads, 5000);
    return () => clearInterval(t);
  }, []);

  // Auto-select thread when arriving via /app/messages?user=<uid>
  useEffect(() => {
    if (!wantedUid || selected?.user_id === wantedUid) return;
    const t = threads.find((x) => x.friend.user_id === wantedUid);
    if (t) {
      setSelected(t.friend);
      api.patch(`/dm/${t.friend.user_id}/read`).catch(() => {});
      // Clear the query param so it doesn't fight later navigation.
      const next = new URLSearchParams(searchParams);
      next.delete("user");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [wantedUid, threads]);

  const acceptRequest = async (fid) => {
    await api.post(`/friends/accept/${fid}`);
    loadThreads();
  };
  const declineRequest = async (fid) => {
    await api.post(`/friends/decline/${fid}`);
    loadThreads();
  };

  const openThread = async (friend) => {
    setSelected(friend);
    // Mark thread read immediately.
    try { await api.patch(`/dm/${friend.user_id}/read`); loadThreads(); } catch {}
  };

  const hasUnread = threads.some((t) => t.unread_count > 0);

  return (
    <div className="max-w-6xl mx-auto" data-testid="messages-page">
      <header className="mb-5">
        <div className="text-[11px] uppercase tracking-[0.32em] text-[#F2DC8C]/60">
          Command Center · Messages
        </div>
        <h1 className="mt-1 font-display text-[34px] leading-tight"
            style={{
              background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 45%, #8a6a00 100%)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              letterSpacing: "0.02em",
            }}>
          Friends & Chats
        </h1>
      </header>

      <div className="rounded-2xl overflow-hidden flex flex-col lg:flex-row"
           style={{
             background: "linear-gradient(180deg, rgba(26,18,6,0.75) 0%, rgba(5,3,0,0.9) 100%)",
             border: "1px solid rgba(212,175,55,0.4)",
             boxShadow: "0 12px 40px rgba(0,0,0,0.55)",
             minHeight: "70vh",
           }}>
        {/* Left pane */}
        <aside className={"lg:w-[320px] shrink-0 border-b lg:border-b-0 lg:border-r flex flex-col " + (selected ? "hidden lg:flex" : "flex")}
               data-testid="messages-list-pane"
               style={{ borderColor: "rgba(212,175,55,0.35)" }}>
          <div className="p-3 flex items-center gap-2 border-b" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#F2DC8C]/80 flex-1">Friends</div>
            <button
              onClick={() => setAddOpen(true)}
              data-testid="messages-add-friend"
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg font-display uppercase text-[10px] tracking-[0.22em]"
              style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
              <UserPlus size={11}/> Add
            </button>
          </div>

          <div className="flex-1 overflow-y-auto no-scrollbar">
            {requests.incoming.length > 0 && (
              <div className="p-3 space-y-2 border-b" style={{ borderColor: "rgba(212,175,55,0.15)" }}>
                <div className="text-[9px] uppercase tracking-widest text-[#FFD700]/85">
                  {requests.incoming.length} pending request{requests.incoming.length === 1 ? "" : "s"}
                </div>
                {requests.incoming.map((r) => (
                  <div key={r.friendship_id}
                       data-testid={`request-in-${r.friendship_id}`}
                       className="rounded-lg p-2.5 flex items-center gap-2"
                       style={{ background: "rgba(255,215,0,0.06)", border: "1px solid rgba(255,215,0,0.4)" }}>
                    <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                         style={{ border: "1.5px solid #D4AF37" }}>
                      <span className="text-[12px] font-display text-[#FFD700]">
                        {(r.from_name || r.from_username || "?").slice(0, 1).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <UserLink username={r.from_username} className="block">
                        <div className="text-[12px] text-[#F2DC8C] truncate">{r.from_name || "@" + r.from_username}</div>
                        <div className="text-[9px] uppercase tracking-widest text-[#F2DC8C]/60 truncate">@{r.from_username}</div>
                      </UserLink>
                    </div>
                    <button onClick={() => acceptRequest(r.friendship_id)}
                            data-testid={`accept-${r.friendship_id}`}
                            title="Accept"
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
                      <Check size={12} weight="bold" color="#0a0703"/>
                    </button>
                    <button onClick={() => declineRequest(r.friendship_id)}
                            data-testid={`decline-${r.friendship_id}`}
                            title="Decline"
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ border: "1px solid rgba(255,93,115,0.4)" }}>
                      <X size={12} className="text-[#FF5D73]"/>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {loadingList ? (
              <div className="p-6 text-center">
                <CircleNotch size={18} className="animate-spin text-[#FFD700] mx-auto"/>
              </div>
            ) : threads.length === 0 && requests.incoming.length === 0 ? (
              <EmptyList onAdd={() => setAddOpen(true)} unread={hasUnread}/>
            ) : (
              <div className="p-2 space-y-1">
                {threads.map((t) => (
                  <ThreadRow
                    key={t.friend.user_id}
                    thread={t}
                    active={selected?.user_id === t.friend.user_id}
                    onClick={() => openThread(t.friend)}
                  />
                ))}
                {requests.outgoing.length > 0 && (
                  <div className="mt-4 pt-3 border-t" style={{ borderColor: "rgba(212,175,55,0.15)" }}>
                    <div className="text-[9px] uppercase tracking-widest text-[#F2DC8C]/50 mb-1 px-2">
                      Sent · waiting
                    </div>
                    {requests.outgoing.map((r) => (
                      <div key={r.friendship_id} data-testid={`request-out-${r.friendship_id}`}
                           className="px-2 py-1.5 text-[11px] text-[#F2DC8C]/60">
                        @{r.to_username}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>

        {/* Right pane */}
        <section className={"flex-1 min-w-0 " + (selected ? "flex" : "hidden lg:flex")}
                 style={{ flexDirection: "column" }}
                 data-testid="messages-chat-pane">
          {selected ? (
            <ChatThread
              me={user}
              friend={selected}
              onClose={() => setSelected(null)}
              onSent={loadThreads}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-center p-10">
              <div>
                <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center"
                     style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
                  <ChatCircleDots size={22} weight="fill" color="#0a0703"/>
                </div>
                <div className="mt-4 font-display text-[22px] text-[#FFD700]">Pick a friend to chat.</div>
                <div className="mt-1 text-[13px] text-[#F2DC8C]/70">
                  Select a thread on the left, or add a new friend to get started.
                </div>
              </div>
            </div>
          )}
        </section>
      </div>

      <AnimatePresence>
        {addOpen && (
          <AddFriendModal
            onClose={() => setAddOpen(false)}
            onChanged={loadThreads}
          />
        )}
      </AnimatePresence>
    </div>
  );
}


function EmptyList({ onAdd }) {
  return (
    <div className="p-6 text-center">
      <div className="text-[13px] text-[#F2DC8C]/70 mb-3">
        No friends yet.
      </div>
      <button onClick={onAdd}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl font-display uppercase text-[10px] tracking-[0.22em]"
              style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
        <UserPlus size={11}/> Add Friend
      </button>
    </div>
  );
}


function ThreadRow({ thread, active, onClick }) {
  const { friend, unread_count, last_message } = thread;
  return (
    <button
      onClick={onClick}
      data-testid={`thread-${friend.user_id}`}
      className="w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-colors"
      style={active
        ? { background: "rgba(255,215,0,0.10)", border: "1px solid rgba(255,215,0,0.55)" }
        : { border: "1px solid transparent" }}>
      <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center shrink-0"
           style={{ border: "1.5px solid #D4AF37" }}>
        {friend.avatar_image ? (
          <img src={`${API}/files/${friend.avatar_image}`} alt="" className="w-full h-full object-cover"/>
        ) : (
          <span className="text-[13px] font-display text-[#FFD700]">
            {(friend.name || friend.username || "?").slice(0, 1).toUpperCase()}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <div className="text-[13px] text-[#F2DC8C] truncate">{friend.name || "@" + friend.username}</div>
          {unread_count > 0 && (
            <span className="ml-auto text-[9px] tabular-nums font-display px-1.5 rounded-full"
                  style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}
                  data-testid={`thread-unread-${friend.user_id}`}>
              {unread_count}
            </span>
          )}
        </div>
        <div className="text-[11px] text-[#F2DC8C]/60 truncate">
          {last_message?.body || <span className="text-[#F2DC8C]/40">Say hi 👋</span>}
        </div>
      </div>
    </button>
  );
}


function ChatThread({ me, friend, onClose, onSent }) {
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/dm/${friend.user_id}/messages`);
      setMsgs(data || []);
    } catch {}
    setLoading(false);
  };
  useEffect(() => {
    setLoading(true);
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [friend.user_id]);
  useEffect(() => {
    // Mark read when opened or new messages arrive.
    api.patch(`/dm/${friend.user_id}/read`).catch(() => {});
  }, [friend.user_id, msgs.length]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs.length]);

  const send = async (e) => {
    e?.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/dm/${friend.user_id}`, { body });
      setMsgs((p) => [...p, data]);
      setBody("");
      onSent?.();
    } finally { setBusy(false); }
  };

  return (
    <>
      {/* Thread header */}
      <div className="p-3 flex items-center gap-3 border-b" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
        <button onClick={onClose} data-testid="chat-close"
                className="lg:hidden w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ border: "1px solid rgba(212,175,55,0.5)" }}>
          <ArrowLeft size={12} className="text-[#FFD700]"/>
        </button>
        <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center shrink-0"
             style={{ border: "1.5px solid #D4AF37" }}>
          {friend.avatar_image ? (
            <img src={`${API}/files/${friend.avatar_image}`} alt="" className="w-full h-full object-cover"/>
          ) : (
            <span className="text-[13px] font-display text-[#FFD700]">
              {(friend.name || friend.username || "?").slice(0, 1).toUpperCase()}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <UserLink username={friend.username} className="block">
            <div className="text-[15px] font-display text-[#FFD700] truncate">
              {friend.name || "@" + friend.username}
            </div>
          </UserLink>
          <div className="text-[10px] uppercase tracking-widest text-[#F2DC8C]/55 flex items-center gap-1">
            <DotOutline size={12} weight="fill" className="text-[#3DDC84]"/> Online · Friend
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0"
           data-testid="chat-scroll">
        {loading ? (
          <div className="pt-10 text-center"><CircleNotch size={18} className="animate-spin text-[#FFD700] mx-auto"/></div>
        ) : msgs.length === 0 ? (
          <div className="pt-16 text-center text-[13px] text-[#F2DC8C]/55">
            No messages yet. Start the conversation.
          </div>
        ) : msgs.map((m) => {
          const mine = m.from_id === me.user_id;
          return (
            <div key={m.msg_id}
                 data-testid={`chat-msg-${m.msg_id}`}
                 className={"flex " + (mine ? "justify-end" : "justify-start")}>
              <div className="max-w-[75%] rounded-2xl px-3 py-2"
                   style={mine
                     ? {
                         background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)",
                         color: "#0a0703",
                         boxShadow: "0 6px 18px rgba(255,215,0,0.25)",
                       }
                     : {
                         background: "rgba(26,18,6,0.75)",
                         border: "1px solid rgba(212,175,55,0.4)",
                         color: "#F2DC8C",
                       }}>
                <div className="text-[13px] whitespace-pre-wrap break-words leading-relaxed">
                  {m.body}
                </div>
                <div className="mt-0.5 text-[9px] uppercase tracking-widest"
                     style={{ opacity: mine ? 0.7 : 0.55 }}>
                  {new Date(m.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <form onSubmit={send} className="p-3 border-t flex gap-2"
            style={{ borderColor: "rgba(212,175,55,0.35)" }}>
        <input
          data-testid="chat-composer"
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, 4000))}
          placeholder={`Message ${friend.name?.split(" ")[0] || friend.username}…`}
          className="flex-1 bg-transparent px-3 py-2 rounded-lg text-[13px] text-[#F2DC8C] placeholder:text-[#F2DC8C]/40 focus:outline-none"
          style={{ border: "1px solid rgba(212,175,55,0.4)" }}
        />
        <button type="submit" disabled={busy || !body.trim()} data-testid="chat-send"
                className="w-10 h-10 rounded-lg flex items-center justify-center disabled:opacity-40"
                style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
          <PaperPlaneRight size={14} weight="fill" color="#0a0703"/>
        </button>
      </form>
    </>
  );
}


function AddFriendModal({ onClose, onChanged }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/friends/search", { params: { q } });
        if (!cancelled) setResults(data || []);
      } catch {}
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const invite = async (u) => {
    setBusy(true);
    try {
      await api.post("/friends/request", { username: u.username });
      // Optimistically update result row.
      setResults((prev) => prev.map((r) => r.user_id === u.user_id ? { ...r, friend_status: "pending" } : r));
      onChanged?.();
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Could not send request.");
    } finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={onClose}
                className="fixed inset-0 z-[110] flex items-center justify-center p-4"
                style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(14px)" }}
                data-testid="add-friend-modal">
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-full max-w-md rounded-2xl overflow-hidden"
                  style={{
                    background: "linear-gradient(180deg, rgba(20,14,4,0.98) 0%, rgba(5,3,0,1) 100%)",
                    border: "1.5px solid rgba(255,215,0,0.55)",
                    boxShadow: "0 30px 80px rgba(0,0,0,0.75)",
                  }}>
        <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
          <UserPlus size={16} weight="fill" className="text-[#FFD700]"/>
          <div className="font-display text-[16px] text-[#FFD700]">Add a Friend</div>
          <button type="button" onClick={onClose}
                  data-testid="add-friend-close"
                  className="ml-auto w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ border: "1px solid rgba(212,175,55,0.5)" }}>
            <X size={13} className="text-[#FFD700]"/>
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="rounded-xl px-3 flex items-center gap-2"
               style={{ border: "1px solid rgba(212,175,55,0.4)", background: "rgba(0,0,0,0.35)" }}>
            <MagnifyingGlass size={14} className="text-[#FFD700]"/>
            <input autoFocus data-testid="add-friend-search"
                   value={q}
                   onChange={(e) => setQ(e.target.value)}
                   placeholder="Search @username or name"
                   className="w-full bg-transparent py-2 text-[14px] text-[#F2DC8C] placeholder:text-[#F2DC8C]/40 focus:outline-none"/>
          </div>
          <div className="max-h-[50vh] overflow-y-auto space-y-1">
            {results.length === 0 && q.trim() && (
              <div className="text-[12px] text-[#F2DC8C]/55 text-center py-6">
                No matches.
              </div>
            )}
            {results.map((u) => (
              <div key={u.user_id} data-testid={`search-${u.username}`}
                   className="flex items-center gap-3 p-2 rounded-lg"
                   style={{ border: "1px solid rgba(212,175,55,0.25)" }}>
                <div className="w-9 h-9 rounded-full overflow-hidden flex items-center justify-center shrink-0"
                     style={{ border: "1.5px solid #D4AF37" }}>
                  {u.avatar_image ? (
                    <img src={`${API}/files/${u.avatar_image}`} alt="" className="w-full h-full object-cover"/>
                  ) : (
                    <span className="text-[12px] font-display text-[#FFD700]">
                      {(u.name || u.username || "?").slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <UserLink username={u.username} className="block">
                    <div className="text-[13px] text-[#F2DC8C] truncate">{u.name || "@" + u.username}</div>
                    <div className="text-[9px] uppercase tracking-widest text-[#F2DC8C]/55">@{u.username}</div>
                  </UserLink>
                </div>
                {u.friend_status === "accepted" ? (
                  <span className="text-[10px] uppercase tracking-widest text-[#3DDC84]">Friends</span>
                ) : u.friend_status === "pending" ? (
                  <span className="text-[10px] uppercase tracking-widest text-[#F2DC8C]/60">Pending</span>
                ) : (
                  <button onClick={() => invite(u)}
                          disabled={busy}
                          data-testid={`invite-${u.username}`}
                          className="h-8 px-3 rounded-lg font-display uppercase text-[10px] tracking-[0.22em]"
                          style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
                    Add
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
